function opComponents() {

    class OpTabs extends HTMLElement {
        connectedCallback() {
            this.tabList = this.querySelector(`[role="tablist"]`);
            this.tabButtons = this.querySelectorAll(`[role="tab"]`);
            this.tabPanels = this.querySelectorAll(`[role="tabpanel"]`);
            this.underlineEl = this.querySelector("[data-tabs-underline]");

            this.tabButtons.forEach(tb => tb.addEventListener("click", this.tabBtn_clickHandler));

            if (this.tabButtons.length > 0) {
                this.selectTab(this.tabButtons[0]);
            }
        }

        disconnectedCallback() {
            this.tabButtons.forEach(tb => tb.removeEventListener("click", this.tabBtn_clickHandler));
        }

        tabBtn_clickHandler = (event) => {
            this.selectTab(event.currentTarget);
        }

        selectTab(selTab) {
            this.tabButtons.forEach(tb => {
                const isCurrent = tb === selTab;
                tb.ariaSelected = isCurrent;
                tb.toggleAttribute("data-current", isCurrent);

                if (isCurrent) {
                    this.showPanel(tb.getAttribute("data-tabs-target"));
                    this.updateUnderline(tb.offsetLeft, tb.offsetTop + tb.offsetHeight, tb.offsetWidth);
                }
            });
        }

        showPanel(selector) {
            const target = selector ? this.querySelector(selector) : null;

            this.tabPanels.forEach(tp => {
                tp.toggleAttribute("data-current", tp === target);
            });
        }

        updateUnderline(left, top, width) {
            if (this.underlineEl) {
                this.underlineEl.style.left = `${left}px`;
                this.underlineEl.style.top = `${top - this.underlineEl.offsetHeight}px`;
                this.underlineEl.style.width = `${width}px`;
            }
        }
    }

    class OpRangeSlider extends HTMLElement {
        connectedCallback() {
            this.inputMin = this.querySelector("input[data-slider-in='min']");
            this.inputMax = this.querySelector("input[data-slider-in='max']");
            this.track = this.querySelector("[data-slider-track]");
            this.range = this.querySelector("[data-slider-range]");
            this.labels = this.querySelectorAll("[data-slider-lb]");
            this.stepDisplay = this.querySelector("[data-step-display]");

            this.limitMin = Number(this.dataset.min) || 0;
            this.limitMax = Number(this.dataset.max) || 100;
            this.minRange = Number(this.dataset.minRange) || 0;
            this.stepGuides = Number(this.dataset.stepGuides) || 0;
            this.minVal = Number(this.inputMin?.value) || this.limitMin;
            this.maxVal = Number(this.inputMax?.value) || this.limitMax;

            if (this.inputMin) {
                this.inputMin.setAttribute("min", this.limitMin);
                this.inputMin.setAttribute("max", this.limitMax);
                this.inputMin.addEventListener("input", this.minInputHandler);
            }

            if (this.inputMax) {
                this.inputMax.setAttribute("min", this.limitMin);
                this.inputMax.setAttribute("max", this.limitMax);
                this.inputMax.addEventListener("input", this.maxInputHandler);
            }

            this.addEventListener("click", this.clickHandler);
            this.setCurrentAttr("min");
            this.updateStepDisplay();
            this.updateRange();
        }

        disconnectedCallback() {
            this.inputMin?.removeEventListener("input", this.minInputHandler);
            this.inputMax?.removeEventListener("input", this.maxInputHandler);
            this.removeEventListener("click", this.clickHandler);
        }

        clickHandler = (e) => {
            const currSlider = e.target.getAttribute("data-slider-in") || e.target.getAttribute("data-slider-lb");

            if (currSlider) {
                this.setCurrentAttr(currSlider);
            }
        }

        maxInputHandler = (e) => {
            const value = Math.max(Number(e.target.value), this.minVal + this.minRange);

            this.maxVal = value;
            e.target.value = value;

            this.setCurrentAttr("max");
            this.updateRange();
        }

        minInputHandler = (e) => {
            const value = Math.min(Number(e.target.value), this.maxVal - this.minRange);

            this.minVal = value;
            e.target.value = value;

            this.setCurrentAttr("min");
            this.updateRange();
        }

        getPercent(val) {
            return Math.round(((val - this.limitMin) / (this.limitMax - this.limitMin)) * 100);
        }

        setCurrentAttr(currentSlider) {
            this.inputMin?.toggleAttribute("data-current", currentSlider === "min");
            this.inputMax?.toggleAttribute("data-current", currentSlider === "max");

            this.labels.forEach(lb => {
                lb.toggleAttribute("data-current", lb.getAttribute("data-slider-lb") === currentSlider);
            });
        }

        updateRange() {
            const minPerc = this.getPercent(this.minVal);
            const maxPerc = this.getPercent(this.maxVal);

            if (this.range) {
                this.range.style.left = `${minPerc}%`;
                this.range.style.width = `${maxPerc - minPerc}%`;
            }

            this.labels.forEach(lb => {
                switch (lb.getAttribute("data-slider-lb")) {
                    case "min":
                        lb.style.left = `${minPerc}%`;
                        lb.textContent = this.minVal;
                        break;
                    case "max":
                        lb.style.left = `${maxPerc}%`;
                        lb.textContent = this.maxVal;
                        break;
                }
            });
        }

        updateStepDisplay() {
            const numStep = this.stepGuides;
            let stepHTML = "";

            if (!this.stepDisplay || !numStep) {
                return;
            }

            for (let i = 0; i <= numStep; i++) {
                const primary = i === 0 || i === numStep / 2 || i === numStep;
                stepHTML += `<span data-step-index="${i}" ${primary ? "data-step-primary" : ""}></span>`;
            }

            this.stepDisplay.innerHTML = stepHTML;
        }
    }

    class OpTextField extends HTMLElement {
        connectedCallback() {
            this.input = this.querySelector('input');
            this.textArea = this.querySelector('textarea');
            this.emptyField = this.querySelector('.js-empty-field')
            this.characterCounter = this.querySelector('.js-character-counter');
            this.dropdownOptions = this.querySelectorAll('.js-dropdown-option');
            this.showPickerBtn = this.querySelector(".js-show-picker");
            this.showPassBtn = this.querySelector(".js-show-pass");

            // Listen to changes on input to show/hide clear button
            this.input?.addEventListener('input', this.inputHandler);

            // Clear button click event
            this.emptyField?.addEventListener('pointerdown', this.emptyFieldHandler);
            this.showPickerBtn?.addEventListener("click", this.pickerBtnHandler);
            this.showPassBtn?.addEventListener("click", this.showPassBtnHandler);

            // Text area input listener (to count characters)
            if (this.textArea && this.textArea.maxLength > 0) {
                this.textArea.addEventListener('input', this.textAreaHandler);
            }

            // Dropdown options select listener
            this.dropdownOptions.forEach((span) => {
                span.addEventListener("pointerdown", this.dropdownOptionHandler);
            });
        }

        disconnectedCallback() {
            this.input?.removeEventListener('input', this.inputHandler);
            this.textArea?.removeEventListener('input', this.textAreaHandler);
            this.emptyField?.removeEventListener('pointerdown', this.emptyFieldHandler);
            this.showPickerBtn?.removeEventListener("click", this.pickerBtnHandler);
            this.showPassBtn?.removeEventListener("click", this.showPassBtnHandler);

            this.dropdownOptions.forEach((span) => {
                span.removeEventListener("pointerdown", this.dropdownOptionHandler);
            });
        }

        inputHandler = () => {
            this.emptyField.style.display = this.input.value.length > 0 ? "block" : "none";
        }

        textAreaHandler = () => {
            const nrOfCharacters = this.textArea.value.length;
            this.characterCounter.textContent = nrOfCharacters;
        }

        emptyFieldHandler = () => {
            if (this.input) {
                this.input.value = "";
                this.input.closest("form")?.dispatchEvent(new Event('input'));
                this.emptyField.style.display = "none";
                this.input.dispatchEvent(new CustomEvent("empty"));
            }
        }

        dropdownOptionHandler = (event) => {
            const value = event.target?.textContent;

            if (this.input && value) {
                this.input.value = value;
                this.inputHandler();
                this.input.dispatchEvent(new Event('input', { bubbles: true }));
                this.input.dispatchEvent(new Event('change', { bubbles: true }));
            }
        }

        pickerBtnHandler = () => {
            if (this.input && !this.input.disabled && !this.input.readOnly) {
                this.input.showPicker();
            }
        }

        showPassBtnHandler = () => {
            if (!this.input) return;

            const isCurrentlyPassword = this.input.type === "password";
            this.input.type = isCurrentlyPassword ? "text" : "password";

            // Trova le icone dentro al bottone
            const eyeIcon = this.showPassBtn.querySelector(".icon-eye");
            const eyeSlashIcon = this.showPassBtn.querySelector(".icon-eye-slash");

            if (eyeIcon && eyeSlashIcon) {
                eyeIcon.classList.toggle("tw-hidden", !isCurrentlyPassword);
                eyeSlashIcon.classList.toggle("tw-hidden", isCurrentlyPassword);
            }
        };

    }

    class OpSlideShow extends HTMLElement {
        connectedCallback() {
            const intervalSeconds = parseInt(this.dataset.interval) || 5;
            let currentIndex = 0;

            this.slides = this.querySelectorAll("[data-slide]");
            this.tooltip = this.querySelector("[data-ct-tooltip]");
            this.videoThumbnails = [...this.querySelectorAll("op-video-with-thumbnail")];

            this.interval = setInterval(() => {
                if (this.videoThumbnails.length === 0 || this.videoThumbnails.every(vt => vt.isPaused)) {
                    currentIndex = currentIndex < this.slides.length - 1 ? currentIndex + 1 : 0;
                    this.showSlide(currentIndex);
                }
            }, intervalSeconds * 1000);

            if (this.tooltip) {
                this.addEventListener("mousemove", this.mouseMoveHandler);
                this.addEventListener("mouseleave", this.mouseExitHandler);
            }

            this.showSlide(currentIndex);
        }

        disconnectedCallback() {
            clearInterval(this.interval);

            if (this.tooltip) {
                this.removeEventListener("mousemove", this.mouseMoveHandler);
                this.removeEventListener("mouseleave", this.mouseExitHandler);
            }
        }

        showSlide(showIndex) {
            for (let i = 0; i < this.slides.length; i++) {
                const isCurrent = i === showIndex;
                const slide = this.slides[i];

                slide.style.display = isCurrent ? "block" : "none";

                if (isCurrent) {
                    this.updateTooltip(slide.getAttribute("data-tooltip-text"));
                }
            }
        }

        updateTooltip(text = "") {
            const isEmpty = !text || text.trim() === "";

            if (this.tooltip) {
                this.tooltip.textContent = text;
                this.tooltip.toggleAttribute("data-visible", !isEmpty);

                if (isEmpty) {
                    this.tooltip.style.display = "none";
                }
            }
        }

        mouseMoveHandler = (event) => {
            if (this.tooltip?.hasAttribute("data-visible")) {
                this.tooltip.style.left = `${event.clientX + 20}px`;
                this.tooltip.style.top = `${event.clientY + 20}px`;
                this.tooltip.style.display = "block";
            }
        }

        mouseExitHandler = () => {
            if (this.tooltip) {
                this.tooltip.style.display = "none";
            }
        }
    }

    class OpCountdown extends HTMLElement {
        connectedCallback() {
            this.originalHTML = this.innerHTML;
            this.cdPlaceholder = this.dataset.cdPlaceholder || "%countdown%";
            this.timezone  = this.dataset.timezone || 'UTC';
            let tmp_now = this.formatDateTime(Date.now());
            this.now = this.parseDateTZ(tmp_now, this.timezone);
            this.startDate = this.parseDateTZ(this.dataset.startTime, this.timezone);
            this.endDate   = this.parseDateTZ(this.dataset.endTime,   this.timezone);
            this.interval = setInterval(this.runCountdownInterval.bind(this), 1000);
            if (this.startDate === -1 || (this.startDate !== -1 && this.now > this.startDate)) {
                this.runCountdownInterval();
                this.classList.replace("tw-hidden", "tw-block");
            }
        }

        disconnectedCallback() {
            clearInterval(this.interval);
        }

        displayError(message) {
            if (window.Shopify?.designMode) {
                this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, message);
            }
            console.error("Countdown:", message);
        }

        formatTime(time) {
            const hours = Math.floor(time / (1000 * 60 * 60));
            const minutes = Math.floor((time % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((time % (1000 * 60)) / 1000);

            const timeParts = [];
            if (hours > 0) {
                timeParts.push(`${hours}H`.padStart(3, '0'));
            }
            if (hours > 0 || minutes > 0) {
                timeParts.push(`${minutes}M`.padStart(3, '0'));
            }
            if (hours > 0 || minutes > 0 || seconds > 0) {
                timeParts.push(`${seconds}S`.padStart(3, '0'));
            }

            if (timeParts.length === 0) {
                clearInterval(this.interval);
                return '';
            }

            return timeParts.join(':');
        }

        parseDateTZ(rawDate = "", tz = "UTC") {
            if (!rawDate) { this.displayError("No date provided!"); return -1; }

            const cleaned = rawDate.trim().replace('T', ' ');
            const [d, t = "00:00:00"] = cleaned.split(" ");
            const [Y, M, D] = d.split("-").map(Number);
            const [h = 0, m = 0, s = 0] = t.split(":").map(Number);

            try {
                // Converts "wall time" in 'tz' -> UTC epoch
                const dtf = new Intl.DateTimeFormat('en-US', {
                    timeZone: tz, year:'numeric', month:'2-digit', day:'2-digit',
                    hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false
                });
                // Create a "fake" date to use formatToParts
                const fake = new Date(Date.UTC(Y, M - 1, D, h, m, s));
                const parts = Object.fromEntries(dtf.formatToParts(fake).map(p => [p.type, p.value]));
                return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
            } catch (e) {
                this.displayError("Invalid timezone or date!");
                return -1;
            }
        }

        runCountdownInterval() {
            if (this.endDate !== -1 && this.originalHTML.includes(this.cdPlaceholder)) {
                this.updateCountdown();
            }
        }

        updateCountdown() {
            let tmp_now = this.formatDateTime(Date.now());
            this.now = this.parseDateTZ(tmp_now, this.timezone);
            const timeRemaining = this.endDate - this.now;
            if (timeRemaining <= 0) {
                this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, '');
                clearInterval(this.interval);
                return;
            }
            const formattedTime = this.formatTime(Math.max(0, timeRemaining));
            this.innerHTML = this.originalHTML.replace(this.cdPlaceholder, formattedTime);
        }

        formatDateTime(timestamp) {
            const date = new Date(timestamp);
            const pad = (n) => (n < 10 ? '0' + n : n);
            const year = date.getFullYear();
            const month = pad(date.getMonth() + 1);
            const day = pad(date.getDate());
            const hours = pad(date.getHours());
            const minutes = pad(date.getMinutes());
            const seconds = pad(date.getSeconds());
            return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
        }
    }
    class OpQuantitySelector extends HTMLElement {
        connectedCallback() {
            this.input = this.querySelector("input");
            this.quantityAdd = this.querySelector("[data-quantity-add]");
            this.quantitySub = this.querySelector("[data-quantity-sub]");
            this.quantityAdd?.addEventListener("click", this.quantityAddHandler);
            this.quantitySub?.addEventListener("click", this.quantitySubHandler);
        }

        quantityAddHandler = () => {
            this.input.value = Math.max(0, Number(this.input.value) + 1);
        }

        quantitySubHandler = () => {
            this.input.value = Math.max(0, Number(this.input.value) - 1);
        }
    }

    class OpPasswordChecker extends HTMLElement {
        connectedCallback() {
            this.input = document.querySelector(this.dataset.input);
            this.inputConfirm = document.querySelector(this.dataset.inputConfirm);
            this.clearButton = this.input.closest("op-text-field")?.querySelector(".js-empty-field");
            this.lengthOutput = this.querySelector("[data-password-checker='length']");
            this.uppercaseOutput = this.querySelector("[data-password-checker='uppercase']");
            this.lowercaseOutput = this.querySelector("[data-password-checker='lowercase']");
            this.numberOutput = this.querySelector("[data-password-checker='number']");
            this.specialOutput = this.querySelector("[data-password-checker='special']");
            this.pswMismatchMsg = document.querySelector(".js-pass-mismatch");
            this.passwordVerified = false;
            this.waitForInputs = false;
            this.requirements = {
                length: false,
                uppercase: false,
                lowercase: false,
                number: false,
                special: false,
                passwordConfirmed: false,
            }

            this.input?.addEventListener("input", this.checkPassword.bind(this));
            this.inputConfirm?.addEventListener("input", this.checkPassword.bind(this));
            this.clearButton?.addEventListener("pointerdown", this.checkPassword.bind(this));
        }

        checkPassword() {
            this.lengthCheck();
            this.uppercaseCheck();
            this.lowercaseCheck();
            this.numberCheck();
            this.specialCheck();
            this.checkConfirmPassword();
            this.toggleSubmitButton();
        }

        toggleSubmitButton() {
            const submitButton = this.closest("form").querySelector("[data-submit-form]");
            const isDisabled = Object.values(this.requirements).includes(false);
            this.passwordVerified = !isDisabled;
            if (!this.waitForInputs) submitButton.disabled = isDisabled;
        }

        lengthCheck() {
            const condition = this.input.value.length >= 8;
            this.checkCondition(condition, "length", this.lengthOutput);
        }

        uppercaseCheck() {
            const condition = /[A-Z]/.test(this.input.value);
            this.checkCondition(condition, "uppercase", this.uppercaseOutput);
        }

        lowercaseCheck() {
            const condition = /[a-z]/.test(this.input.value);
            this.checkCondition(condition, "lowercase", this.lowercaseOutput);
        }

        numberCheck() {
            const condition = /[0-9]/.test(this.input.value);
            this.checkCondition(condition, "number", this.numberOutput);
        }

        specialCheck() {
            const condition = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.input.value);
            this.checkCondition(condition, "special", this.specialOutput);
        }

        checkCondition(condition, key, output) {
            const verifCheck = output?.querySelector(".verification-check");
            const verifNeutral = output?.querySelector(".verification-neutral");

            if (condition) {
                this.requirements[key] = true;
                verifCheck?.classList.remove("tw-hidden");
                verifNeutral?.classList.add("tw-hidden");
            } else {
                this.requirements[key] = false;
                verifCheck?.classList.add("tw-hidden");
                verifNeutral?.classList.remove("tw-hidden");
            }
        }

        checkConfirmPassword() {
            if (!this.inputConfirm) return;

            const passConfirmed = this.input.value === this.inputConfirm.value;

            this.checkCondition(passConfirmed, "passwordConfirmed");

            if (this.input.value && this.inputConfirm.value) {
                this.inputConfirm.closest("op-text-field")?.classList.toggle("tw-op-textfield-error", !passConfirmed);
            }

            this.inputConfirm.addEventListener("focus", (event) => this.toggleMismatchMessage(event));
            this.inputConfirm.addEventListener("blur", (event) => this.toggleMismatchMessage(event));
            this.input.addEventListener("input", (event) => this.toggleMismatchMessage(event));

        }

        toggleMismatchMessage(event) {
            if (!this.pswMismatchMsg || !this.input.value) return;

            if (event.type === "focus") {
                this.pswMismatchMsg.dataset.visible = "false";
            } else {
                if (this.requirements.passwordConfirmed || !this.inputConfirm.value) {
                    this.pswMismatchMsg.dataset.visible = "false";
                } else {
                    this.pswMismatchMsg.dataset.visible = "true";
                }
            }
            
        }
    }

    class OpPasswordCheckerContainer extends HTMLElement {
        constructor() {
            super();
            this.checkerContainer = this.getElementsByClassName("password-checker-container")[0];
            this.passwordInput = document.getElementById(this.dataset.customerPassword);

            this.init();
        }

        init() {
            this.passwordInput.addEventListener("input", () => this.toggleVisibility());
        }

        toggleVisibility() {
            if (this.passwordInput.value.length > 0) {
                this.checkerContainer.classList.remove("tw-hidden");
            } else {
                this.checkerContainer.classList.add("tw-hidden");
            }
        }

        disconnectedCallback() {
            this.passwordInput.removeEventListener("input", this.toggleVisibility);
        }
    }

    class OpForm extends HTMLElement {
        connectedCallback() {
            this.form = this.querySelector("form");
            this.submitButton = this.form.querySelector("[data-submit-form]");
            this.passwordChecker = this.form.querySelector("op-password-checker");
            this.allInputsFilled = false;
            this.birthDateInputsFilled = false;
            this.ageRequired = false;

            if (!this.form || !this.submitButton) return;

            // Customer tags - used in main register
            this.customerTags = this.form.querySelector('[name="customer[tags]"]');
            this.customerTagInputs = this.form.querySelectorAll("[data-customer-tag]");
            if (this.customerTagInputs.length > 0 && this.customerTags) this.setupCustomerTagListeners();

            this.form.addEventListener('input', this.inputHandler);
            // this.form.addEventListener('input', this.submitHandler);
            this.form.querySelector('button[data-submit-form]').addEventListener('click', this.submitHandler);

        }

        disconnectedCallback() {
            this.form?.removeEventListener("input", this.inputHandler);
        }

        inputHandler = (event) => {
            if (event.target.type === "tel") {
                event.target.value = event.target.value.replace(/[^0-9]/g, '');
            }

            this.allInputsFilled = Array.from(this.form.querySelectorAll('input[required]')).every(input => input.value.trim() !== '');
            // Reset Error
            this.form.querySelectorAll('input:not([required])').forEach(input => input.closest('op-text-field')?.classList.remove('tw-op-textfield-error'));
            this.form.querySelector('.birthday-input-wrapper .age-error-msg')?.classList.add('tw-hidden');
            this.toggleSubmitButton();
        }

        submitHandler = (event) => {
            // event.preventDefault();
            if (this.submitButton.disabled) return;
            this.birthDateInputsFilled = Array.from(this.form.querySelectorAll('input:not([required])')).every(input => input.value.trim() !== '');
            if(this.dataset.ageValidation == "true" && this.birthDateInputsFilled){
                this.validateDateOfBirth(); 
            } 
            if(!this.ageRequired) {
                localStorage.setItem("is-account-new", true);
                this.saveCustomerData();
                this.form.submit();
            }

        }

        validateDateOfBirth() {
            const dayInput = this.form.querySelector('[data-customer-tag="birthday-day"]');
            const monthInput = this.form.querySelector('[data-customer-tag="birthday-month"]');
            const yearInput = this.form.querySelector('[data-customer-tag="birthday-year"]');
            const ageErrorMsg = this.form.querySelector('.birthday-input-wrapper .age-error-msg');

            if (!dayInput || !monthInput || !yearInput) return;

            const day = dayInput.value.trim();
            const month = monthInput.value.trim();
            const monthIndex = month - 1
            const year = yearInput.value.trim();
            const selectedDate = new Date(year, monthIndex, day);
            
            // Get the current date
            const currentDate = new Date();
            // Calculate the age
            const age = currentDate.getFullYear() - selectedDate.getFullYear() - 
             (currentDate.getMonth() < selectedDate.getMonth() || 
             (currentDate.getMonth() === selectedDate.getMonth() && currentDate.getDate() < selectedDate.getDate()))
            // Check if the user is 18 or older
            if (age < 18) {
                this.ageRequired = true;
                
                dayInput.closest('op-text-field').classList.add('tw-op-textfield-error');
                monthInput.closest('op-text-field').classList.add('tw-op-textfield-error');
                yearInput.closest('op-text-field').classList.add('tw-op-textfield-error');
                ageErrorMsg.classList.remove('tw-hidden');
                dayInput.scrollIntoView({ behavior: "smooth", block: "end", inline: "nearest" });
            } else {
                this.ageRequired = false;
            }
        }

        toggleSubmitButton() {
            if (this.passwordChecker) {
                this.submitButton.disabled = !(this.passwordChecker.passwordVerified && this.allInputsFilled);
            } else {
                this.submitButton.disabled = !this.allInputsFilled;
            }
        }

        setupCustomerTagListeners = () => {
            const customerTagHandler = (event) => {
                const input = event.target;

                const value = event.target.type === "checkbox" ? event.target.checked : event.target.value.trim();
                const tagKey = input.dataset.customerTag;
                if (!tagKey) return

                if (tagKey === 'birthday-day' || tagKey === 'birthday-month' || tagKey === 'birthday-year') {
                    this.handleBirthdayTags();
                    return;
                }


                const tag = `${tagKey}:${value}`;
                let tagsArr = this.customerTags.value
                    .split(",")
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0);

                if (tagsArr.some(tag => tag.includes(tagKey))) {
                    tagsArr = tagsArr.map((tag) => {
                        if (tag.startsWith(tagKey)) {
                            return value !== "" ? `${tagKey}:${value}` : "";
                        } else {
                            return tag;
                        }
                    }).filter(tag => tag.length > 0);
                } else {
                    tagsArr.push(tag);
                }

                this.customerTags.value = tagsArr.join(",");
            };

            this.customerTagInputs.forEach((input) => {
                input.addEventListener("change", customerTagHandler);
                input.addEventListener("empty", customerTagHandler);
            });
        }

        handleBirthdayTags = () => {
            const dayInput = this.form.querySelector('[data-customer-tag="birthday-day"]');
            const monthInput = this.form.querySelector('[data-customer-tag="birthday-month"]');
            const yearInput = this.form.querySelector('[data-customer-tag="birthday-year"]');

            if (!dayInput || !monthInput || !yearInput) return;

            const day = dayInput.value.trim();
            const month = monthInput.value.trim();
            const year = yearInput.value.trim();

            let tagsArr = this.customerTags.value
                .split(",")
                .map(tag => tag.trim())
                .filter(tag => tag.length > 0);

            tagsArr = tagsArr.filter(tag => !tag.startsWith('birthday-day:') && !tag.startsWith('birthday-month:') && !tag.startsWith('birthday-year:') && !tag.startsWith('birth:'));

            /*if (day) tagsArr.push(`birthday-day:${day}`);
            if (month) tagsArr.push(`birthday-month:${month}`);
            if (year) tagsArr.push(`birthday-year:${year}`);*/

            if (day && month && year) {
                const formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
                tagsArr.push(`birth:${formattedDate}`);
            }

            this.customerTags.value = tagsArr.join(",");
        }

        saveCustomerData() {
            const fdata = new FormData(this.form);
            const data = [{
                e: fdata.get("customer[email]"),
                p: fdata.get("customer[password]")
            }]
            const encodedString = btoa(JSON.stringify(data))

            localStorage.setItem('c_profile', encodedString)
        }

    }

    class OpNewsletterForm extends HTMLElement {
        normalizeEmailForHashing(email) {
            if (typeof email !== "string") return "";

            return email.toLowerCase().trim().replace(/\s+/g, "");
        }

        buildEmailMd5(email) {
            const normalizedEmail = this.normalizeEmailForHashing(email);
            if (!normalizedEmail) return "";

            return CryptoJS.MD5(normalizedEmail).toString();
        }

        getTrackingId() {
            return this.dataset.trackingId || this.dataset.sectionid || this.form?.id || this.emailField?.id || "";
        }

        getStoredSubmission() {
            const rawValue = sessionStorage.getItem("newsletterFormSubmitted");
            if (!rawValue) {
                return { email: "", trackingId: "" };
            }

            try {
                const parsedValue = JSON.parse(rawValue);
                if (parsedValue && typeof parsedValue === "object") {
                    return {
                        email: this.normalizeEmailForHashing(parsedValue.email),
                        trackingId: typeof parsedValue.trackingId === "string" ? parsedValue.trackingId : ""
                    };
                }
            } catch (error) {
                // Backward compatibility for previously stored plain-string emails.
            }

            return {
                email: this.normalizeEmailForHashing(rawValue),
                trackingId: ""
            };
        }

        getSuccessStateRoot() {
            return this.closest("[data-newsletter-success-root]") || this;
        }

        updateSuccessState() {
            const successStateRoot = this.getSuccessStateRoot();
            successStateRoot.querySelectorAll(".form-message").forEach(node => node.remove());
            successStateRoot.querySelectorAll(".thanks-message").forEach(node => node.classList.remove("tw-hidden"));
        }

        connectedCallback() {
            this.form = this.querySelector('form.newsletter-form') || this.querySelector('form');
            if (!this.form) return;

            // this.market = this.dataset.market;
            this.ageValidationRequired = this.dataset.ageValidation == "true";
            console.log('Age validation required:', this.ageValidationRequired);
            this.button = this.querySelector('#Subscribe-page');

            if(this.button) {
                this.button.disabled = false;
            }

            // Elements for newsletter submission tracking
            this.emailField = this.querySelector('input[name="contact[email]"]');
            this.cryptoUrl = this.dataset.cryptoScript || "https://cdnjs.cloudflare.com/ajax/libs/crypto-js/4.2.0/crypto-js.min.js";
            this.trackingId = this.getTrackingId();

            // Setup both tealium tracking and dynamic tags if present
            if (this.emailField) {
                this._initSubmissionTracking();
            }

            // Elements for dynamic customer tags
            this.customerTags = this.form.querySelector('#NewsletterTags');
            this.customerTagInputs = this.form.querySelectorAll('[data-customer-tag]');

            // Elements to interact
            this.dataDialogId = this.dataset.dialogId;
            this.newsletterInnerWrapper = this.querySelector('form .newsletter-form-wrapper');

            // Select the drawer containing the custom element
            if (this.dataDialogId) {
                this.dialog = this.closest(`dialog#${this.dataDialogId}`);
            }

            if (this.customerTagInputs.length && this.customerTags) {
                this._setupCustomerTagListeners();
            }

            const newsletterSaved = sessionStorage.getItem("newsletterSaved");
            if (newsletterSaved === "true" && this.dialog) {
                this.dialog.style.transition = "none"
                this.dialog.showModal()
                this.dialog.addEventListener("close", () => this.dialog.style.removeProperty("transition"), { once: true })
            } 

            //this.formSubmitted();
        }

        _initSubmissionTracking() {
            // On page load, send tealium if session indicates a prior submission
            const { email: sessionEmail, trackingId } = this.getStoredSubmission();
            if (sessionEmail && (!trackingId || trackingId === this.trackingId)) {
                const cryptoScriptLoader = typeof CryptoJS !== "undefined" ? Promise.resolve() : Fetchutils.importScript(this.cryptoUrl);
                cryptoScriptLoader
                    .then(() => {
                        const emailMd5 = this.buildEmailMd5(sessionEmail);
                        if (!emailMd5) return;

                        sessionStorage.setItem("newsletterFormSubmittedMd5", emailMd5);
                        const data = {
                            id: "Event",
                            Events_UserEmailSub: "1",
                            User_Email_MD5: emailMd5
                        };
                        Fetchutils.sendTealiumData(data);
                        sessionStorage.removeItem("newsletterFormSubmitted");
                        this.updateSuccessState();
                    })
                    .catch(error => console.error('Error loading CryptoJS:', error));
            }
            // On form submit, save email to session for tracking
            this.form.addEventListener('submit', () => {
                const email = this.normalizeEmailForHashing(this.emailField.value);
                if (email) {
                    sessionStorage.removeItem("newsletterFormSubmittedMd5");
                    sessionStorage.setItem("newsletterFormSubmitted", JSON.stringify({
                        email,
                        trackingId: this.trackingId
                    }));
                    sessionStorage.setItem("newsletterSaved", "true");
                }
            });
        }

        _setupCustomerTagListeners() {
            const updateAllTags = () => {
                // Keep static tags (without colon)
                const staticTags = this.customerTags.value
                    .split(',')
                    .map(t => t.trim())
                    .filter(t => t && !t.includes(':'));

                const dynamicTags = [];
                this.customerTagInputs.forEach(input => {
                    const key = input.dataset.customerTag;
                    const raw = input.value.trim();
                    if (!raw) return;
                    if (key === 'birthday') {
                        dynamicTags.push(`birth:${raw}`);
                        console.log(raw);
                        if (this.ageValidationRequired) {
                            console.log('Checking age for:', raw);
                            this.checkAge(raw);
                        }
                    } else {
                        dynamicTags.push(`${key}:${raw}`);
                    }
                });

                this.customerTags.value = [...staticTags, ...dynamicTags].join(',');
            };

            this.customerTagInputs.forEach(input => {
                input.addEventListener('change', updateAllTags);
                input.addEventListener('input', updateAllTags);
            });
        }

        checkAge(birthDate) {
            if (!birthDate) {
                this.form.querySelector('.age-error-msg')?.classList.add('tw-hidden');
                if (this.button) this.button.disabled = false;
                return;
            }

            let timestamp = Date.now()-Date.parse(birthDate),
                seconds = Math.floor(timestamp / 1000),
                minutes = Math.floor(seconds / 60),
                hours = Math.floor(minutes / 60),
                days = Math.floor(hours / 24),
                years = Math.floor(days / 365);

            if(this.button) {
                console.log('Calculated age:', years);
                if (years >= 18) {
                    this.form.querySelector('.age-error-msg')?.classList.add('tw-hidden');
                    this.button.disabled = false;
                } else {
                    this.form.querySelector('.age-error-msg')?.classList.remove('tw-hidden');
                    this.button.disabled = true;
                }
            }
        }

        // Check the URL or error class to automatically open the drawer after the redirect.
        formSubmitted() {
            const params = new URLSearchParams(window.location.search);

            if (params.get("customer_posted") == "true" || this.newsletterInnerWrapper.classList.contains('newsletter-form-wrapper--error')) {
                if (this.dialog) {
                    this.dialog.showModal();
                }
                return true;
            }
            return false;
        }

        disconnectedCallback() {
            // Remove listeners if needed
            this.form?.removeEventListener('submit', this._initSubmissionTracking);
            this.customerTagInputs.forEach(input => {
                input.removeEventListener('change', this._setupCustomerTagListeners);
                input.removeEventListener('input', this._setupCustomerTagListeners);
            });
        }
    }

    class OpVideoWithThumbnail extends HTMLElement {
        get isPaused() {
            return this.videoElement?.paused || this.dataset.playing === "false" || this.dataset.playing === "paused";
        }

        connectedCallback() {
            this.videoElement = this.querySelector('video');
            this.playPauseBtn = this.querySelector("[data-play-btn]");
            this.audioMuteBtn = this.querySelector("[data-mute-btn]");

            if (!this.videoElement || !this.playPauseBtn || !this.audioMuteBtn) return;

            this.playPauseBtn.addEventListener("click", this.togglePlayPause.bind(this));
            this.audioMuteBtn.addEventListener("click", this.toggleMute.bind(this));

        }

        togglePlayPause() {
            if (this.videoElement.paused) {
                this.videoElement.play();
                this.dataset.playing = this.dataset.videoMode === "hover" ? "hover" : "true";
            } else {
                this.videoElement.pause();
                this.dataset.playing = this.dataset.videoMode === "autoplay" ? "paused" : "false";
            }
        }

        toggleMute() {
            if (this.videoElement.muted) {
                this.videoElement.muted = false;
                this.dataset.muted = false;

                if (this.dataset.playing === "hover") {
                    this.dataset.playing = true;
                }
            } else {
                this.videoElement.muted = true;
                this.dataset.muted = true;
            }
        }

    }

    class OpModalTrigger extends HTMLElement {
        connectedCallback() {
            this.modal = document.querySelector(`#${this.dataset.modalId}`);
            this.trigger = this.querySelector(".js-modal-trigger") || this.querySelector("button");
            this.second = this.dataset.modalSecond || '';

            this.animation = this.hasAttribute("data-animate");
            this.animateBody = this.dataset.animateBody === "true";
            this.animationClass = this.dataset.animationClass;

            this.trigger?.addEventListener("click", this.triggerClickHandler);

            if (this.animateBody) {
                document.body.classList.add("tw-transition-transform");
                document.body.classList.add("tw-duration-300");
            }

            if (this.animation) {
                this.modal?.addEventListener('close', this.modalCloseHandler);
            }
        }

        disconnectedCallback() {
            this.trigger?.removeEventListener("click", this.triggerClickHandler);

            if (this.animateBody) {
                document.body.classList.remove("tw-transition-transform");
                document.body.classList.remove("tw-duration-300");
            }

            if (this.animation) {
                this.modal?.removeEventListener('close', this.modalCloseHandler);
            }
        }

        triggerClickHandler = () => {
            this.modal?.showModal();

            if (this.dataset.modalSecond === 'true') {
                const nestedBtn = this.modal.querySelector('op-modal-trigger').querySelector('button');
                nestedBtn?.click();
            }

            if (!this.animation) return;
            if (this.animationClass) this.modal?.classList.add(this.animationClass);

            this.modal?.classList.remove("tw-translate-x-full")
            if (this.animateBody) document.body.style.transform = "translateX(-30px)";
        }

        modalCloseHandler = () => {
            this.modal?.classList.add("tw-translate-x-full")
            if (this.animateBody) document.body.style.transform = "translateX(0px)";
            if (this.animationClass) this.modal?.classList.remove(this.animationClass);
        }
    }

    class OpModalCloser extends HTMLElement {
        connectedCallback() {
            this.modal = this.closest("dialog");
            this.backdropClickClose = this.dataset.backdropClose === "true";

            this.addEventListener("click", this.closeModal);

            if (this.backdropClickClose) {
                this.modal?.addEventListener("click", this.backdropClickHandler);
            }

            subscribe(PUB_SUB_EVENTS.closeCliponModal, (payload) => {
                if (this.modal.id == payload.dialogId) {
                    this.closeModal();
                }
            });
        }

        backdropClickHandler = (event) => {
            if (event.target === this.modal) {
                this.closeModal();
            }
        }

        closeModal = () => {
            if (this.modal.dataset.position === "right") {
                this.displayParentsModal(this.modal, true);
                this.modal.classList.remove('tw-animate-slide-left');
                this.modal.classList.add('tw-animate-slide-right');
                setTimeout(() => {
                    this.modal.classList.remove('tw-animate-slide-right');
                    this.modal?.close();
                    this.closeParentModal();
                    this.displayParentsModal(this.modal, false);
                    this.modal.classList.add("tw-animate-slide-left");
                }, 480); // Timeout to allow animation to finish
            } else {
                this.modal?.close();
                this.closeParentModal();
            }
            sessionStorage.removeItem("newsletterSaved");
        }

        closeParentModal = () => {
            let parentModal = this.modal?.parentElement.closest("dialog");
            if (parentModal && parentModal !== this.modal) {
                parentModal.close();
            }
        }

        displayParentsModal(modal, add) {
            const parentModal = modal?.parentElement.closest("dialog");
            if(parentModal && parentModal !== modal) {
                this.displayParentsModal(parentModal, add);
                if(add) parentModal.classList.add("tw-opacity-0");
                else parentModal.classList.remove("tw-opacity-0");
            }
        }

        disconnectedCallback() {
            this.removeEventListener("click", this.closeModal);

            if (this.backdropClickClose) {
                this.modal?.removeEventListener("click", this.backdropClickHandler);
            }
        }
    }

    class ModalOrLinkButton extends HTMLElement {

        constructor() {
            super();
        }

        connectedCallback() {
            this.button = this.querySelector('a');
            this.modalId = this.dataset.modalId;
            this.modalEnabled = this.dataset.modalEnabled === "true";

            if (this.button && this.modalEnabled && this.modalId) {
                this.button.addEventListener('click', this.handleClick.bind(this));
            }
        }

        handleClick(event) {
            event.preventDefault();
            const modal = document.getElementById(this.modalId);
            if (modal && typeof modal.showModal === "function") {
                modal.classList.add("tw-animate-slide-left");
                modal.showModal();
            } else {
                console.error("Modal not found or not a dialog element:", this.modalId);
            }
        }
    }

    class OpSliderComponent extends HTMLElement {
        connectedCallback () {
            this.slider = this.querySelector('[id^="Slider-"]');
            this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
            this.enableSliderLooping = true;
            this.currentPageElement = this.querySelector('.slider-counter--current');
            this.pageTotalElement = this.querySelector('.slider-counter--total');
            this.prevButton = this.querySelector('button[name="previous"]');
            this.nextButton = this.querySelector('button[name="next"]');
            this.slidesPerView = Number(this.dataset.slidesPerView);
            this.thumbnailWrapper = this.querySelector(".slider-thumbnails");
            this.thumbnailItems = this.querySelectorAll('[id^="Thumbnail-"]');
            this.thumbnailDragging = false;
            this.preventWheelScroll = this.dataset.preventWheelScroll === "true";
            this.lastSelectedIndex = 0;

            if (!this.slider || !this.nextButton) return;

            if (this.thumbnailItems.length < 2) {
                this.prevButton.disabled = true;
                this.nextButton.disabled = true;
                return;
            }

            this.initPages();
            const resizeObserver = new ResizeObserver((entries) => this.initPages());
            resizeObserver.observe(this.slider);

            this.slider.addEventListener('scroll', this.update.bind(this));
            this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
            this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
            if (this.thumbnailWrapper && this.thumbnailItems.length > 0) {
                this.pointerScroll(this.thumbnailWrapper);
                this.thumbnailItems[0].dataset.selected = "true";
            }
            if (this.thumbnailItems.length > 0) this.thumbnailItems.forEach((thumbnail) => {
                thumbnail.addEventListener("click", (event) => {
                    this.onThumbnailClick(event, thumbnail);
                });
            });

            this.slider.addEventListener("wheel", (event) => {
                if (this.preventWheelScroll && event.deltaX !== 0) {
                    event.preventDefault();
                }
            }, { passive: false });
        }

        initPages() {
            this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
            if (this.sliderItemsToShow.length < 2) return;
            this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
            this.slidesPerPage = this.slidesPerView > 0 ? this.slidesPerView : Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset);
            this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
            this.update();
        }

        resetPages() {
            this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
            this.initPages();
        }

        update() {
            // Temporarily prevents unneeded updates resulting from variant changes
            // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
            if (!this.slider || !this.nextButton) return;

            const previousPage = this.currentPage;
            const selIndex = Math.round(this.slider.scrollLeft / this.sliderItemOffset);

            this.currentPage = selIndex + 1;

            if (this.currentPageElement && this.pageTotalElement) {
                this.currentPageElement.textContent = this.currentPage;
                // this.pageTotalElement.textContent = this.totalPages;
            }

            if (this.currentPage != previousPage) {
                this.updateSelectedThumbnail(selIndex);

                this.dispatchEvent(
                    new CustomEvent('slideChanged', {
                        detail: {
                            currentPage: this.currentPage,
                            currentElement: this.sliderItemsToShow[selIndex],
                            currentThumbnail: this.thumbnailItems[selIndex],
                        },
                    })
                );
            }

            if(!this.enableSliderLooping) {
                this.prevButton.disabled = this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0;
                this.nextButton.disabled = this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]);
            }
        }

        updateSelectedThumbnail(selIndex) {
            if (this.thumbnailWrapper && this.thumbnailItems.length > 0) {

                this.thumbnailItems.forEach((thumb, index) => {
                    const isCurrent = index === selIndex;
                    thumb.dataset.selected = isCurrent;

                    if (isCurrent) {
                        if (index != this.lastSelectedIndex) {
                            thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                        }

                        this.lastSelectedIndex = index;
                    }
                });
            }
        }

        isSlideVisible(element, offset = 0) {
            const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
            return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
        }

        onButtonClick(event) {
            event.preventDefault();
            const step = event.currentTarget.dataset.step || 1;
            const isNext = event.currentTarget.name === 'next';
            const isPrev = event.currentTarget.name === 'previous';

            if (isNext && this.currentPage === this.sliderItemsToShow.length) {
                this.setSlidePosition(0);
                return;
            }
            if (isPrev && this.currentPage === 1) {
                this.setSlidePosition((this.sliderItemsToShow.length - 1) * this.sliderItemOffset);
                return;
            }

            const slideScrollPos = isNext
                ? this.slider.scrollLeft + step * this.sliderItemOffset
                : this.slider.scrollLeft - step * this.sliderItemOffset;

            this.setSlidePosition(slideScrollPos);
        }

        setSlidePosition(position) {
            this.slider.scrollTo({
                left: position,
            });
        }

        onThumbnailClick(event, thumbnail) {
            event.preventDefault();

            const thumbnailIndex = thumbnail.dataset.index
                ? parseInt(thumbnail.dataset.index)
                : [...thumbnail.parentNode.children].indexOf(thumbnail);

            this.slideScrollPosition = thumbnailIndex * this.sliderItemOffset
            this.setSlidePosition(this.slideScrollPosition);
        }

        pointerScroll(elem) {
            let thumbnail;

            const dragStart = (ev) => {
                ev.preventDefault();
                elem.setPointerCapture(ev.pointerId);
                thumbnail = ev.target;
            }
            const dragEnd = (ev) => {
                ev.preventDefault();
                elem.releasePointerCapture(ev.pointerId);
                if (!this.thumbnailDragging && thumbnail) {
                    thumbnail.click();
                }
                this.thumbnailDragging = false;
            }
            const drag = (ev) => {
                ev.preventDefault();
                if (elem.hasPointerCapture(ev.pointerId)) {
                    elem.scrollLeft -= ev.movementX
                    this.thumbnailDragging = true;
                };
            }

            elem.addEventListener("pointerdown", dragStart);
            elem.addEventListener("pointerup", dragEnd);
            elem.addEventListener("pointermove", drag);
        };
    }

    class OpSwiper extends HTMLElement {
        connectedCallback() {
            this.config = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent ?? "{}");
            this.zoomInButton = this.querySelector("[data-zoom-in]");
            this.zoomOutButton = this.querySelector("[data-zoom-out]");
            if (this.config.init !== true) this.config.init = false;
            this.singleClickZoom = this.dataset.singleClickZoom === "true";
            this.waitForInit = this.dataset.waitForInit === "true";
            this.removeClass = this.dataset.removeClass;
            this.loaderEl = this.querySelector("[data-loader]");
            this.loaded = false;

            this.config.on = this.config.on || {};

            this.config.on.init = this.onSwiperInit.bind(this);

            // Setup single click zoom
            if (this.config.zoom || this.singleClickZoom) {
                this.config.on.click = function (swiper, event) {
                    const zoom = swiper.zoom;
                    if (zoom.scale && zoom.scale !== 1) {
                        zoom.out();
                    } else {
                        zoom.in(event.target);
                    }
                };
            }

            this.swiper = window.Swiper ? new window.Swiper(this, this.config) : null;
            this.control = this.dataset.control ? document.querySelector(this.dataset.control) : null;

            this.dialog = this.closest("op-dialog") || this.closest("dialog");

            this.mutationObserver = new MutationObserver((mutations) => {
                mutations.forEach(({ type, attributeName, target }) => {
                    if (type === "attributes" && attributeName?.includes("open") && target === this.dialog) this.startSwiper();
                });
            });

            if (this.dialog) {
                this.mutationObserver.observe(this.dialog, {
                    attributes: true,
                    attributeFilter: ["open", "data-open"],
                });
            } else if (!this.waitForInit) {
                this.startSwiper();
            }
        }

        startSwiper = () => {
            if (this.loaded) return;

            if (this.swiper) {
                this.swiper.on("afterInit", () => {
                    this.dispatchEvent(new CustomEvent('swiper:afterInit'))
                });

                this.swiper.on("slideChange", () => {
                    this.dispatchEvent(new CustomEvent("slideChanged", {
                        detail: {
                            activeIndex: this.swiper.activeIndex,
                            previousIndex: this.swiper.previousIndex,
                            realIndex: this.swiper.realIndex,
                        }
                    }));

                    if (this.zoomOutButton) this.zoomOutButton.disabled = true;
                    if (this.zoomInButton) this.zoomInButton.disabled = false;
                });

                this.zoomInButton?.addEventListener('click', () => {
                    this.swiper.zoom.in();
                    if (this.zoomInButton) this.zoomInButton.disabled = true;
                    if (this.zoomOutButton) this.zoomOutButton.disabled = false;
                });

                this.zoomOutButton?.addEventListener('click', () => {
                    this.swiper.zoom.out();
                    if (this.zoomOutButton) this.zoomOutButton.disabled = true;
                    if (this.zoomInButton) this.zoomInButton.disabled = false;
                });

                if (this.config && !this.config.init) {
                    this.swiper.init();
                    if (this.control && this.control.swiper) {
                        this.swiper.controller.control = this.control.swiper;
                    } else if (this.control) {
                        this.control.addEventListener('swiper:afterInit', () => {
                            this.swiper.controller.control = this.control.swiper
                            this.swiper.pagination.update();
                        });
                    }
                }

                this.classList.add('visible-swiper-slides');
                this.toggleAttribute("data-loaded", true);
                this.loaded = true;
            }
            else {
                console.error('Failed to load Swiper');
            }
        }

        onSwiperInit () {
            this.toggleAttribute("data-loaded", true);

            if (this.loaderEl) {
                this.loaderEl.style.display = "none";
            }

            if (this.removeClass) {
                this.querySelector(".swiper-wrapper")?.classList.remove(this.removeClass);
            }
        }

        disconnectedCallback() {
            this.swiper?.destroy();
        }
    };

    class OpIntersectionSticky extends HTMLElement {
        connectedCallback() {
            this.placement = this.dataset.placement;
            this.hideTarget = document.getElementById(this.dataset.hideTarget);
            this.threshold = parseFloat(this.dataset.threshold || "0");

            if (this.dataset.hideFooterTarget == "true") {
                this.hideFooterTarget = document.querySelector('footer');
            }

            this.observer = new IntersectionObserver(this.intersectionCallback.bind(this), {
                root: null,
                rootMargin: "0px",
                threshold: this.threshold
            });

            if (this.hideFooterTarget) {
                this.observer.observe(this.hideFooterTarget);
            }

            if (this.hideTarget) {
                this.observer.observe(this.hideTarget);
            }
        }

        disconnectedCallback() {
            this.observer?.disconnect();
        }

        canShow(targetRect) {
            const elementRect = this.getBoundingClientRect();

            switch (this.placement) {
                case "bottom":
                    return targetRect.top > elementRect.top;
                case "top":
                    return targetRect.bottom < elementRect.bottom;
                default:
                    return true;
            }

        }

        intersectionCallback(entries) {
            const isIntersectingArray = entries.map(entry => entry.isIntersecting).filter(entryIntersecting => entryIntersecting);
            const isIntersectingAnything = isIntersectingArray.length > 0;

            entries.forEach((entry) => {
                const targetRect = entry.target.getBoundingClientRect();
                const showSticky = !entry.isIntersecting && this.canShow(targetRect);

                this.classList.toggle("tw-animate-slide-up", showSticky); // Is intersecting
                this.classList.toggle("tw-animate-slide-down", !showSticky); // Is not intersecting

                if (showSticky && !isIntersectingAnything) this.classList.remove("tw-invisible");

                setTimeout(() => this.classList.toggle("tw-hidden", !showSticky), 300);
            });
        }

        hide() {
            this.style.display = "none";
        }

        show() {
            this.style.removeProperty("display");
        }
    };

    class OpDeliveryEstimator extends HTMLElement {
        connectedCallback() {
            this.zip = this.dataset.zip;
            this.country = this.dataset.country;
            this.province = this.dataset.province;
            this.label = this.dataset.label;
            this.appendText = this.dataset.appendText;
            this.hideYear = this.dataset.hideYear === "true";
            this.localeCountry = `${window.Shopify?.locale || "en"}-${window.Shopify?.country || "US"}`;
            this.configEstimationDays = this.parseConfigDays(this.dataset.estimationDays);
            this.preorderDate = this.dataset.preorderDate ? this.parseDate(this.dataset.preorderDate) : null;
            this.startDate = this.dataset.orderDate ? this.dataset.orderDate : new Date();

            if (this.preorderDate && !isNaN(this.preorderDate.getTime()) && this.preorderDate > this.startDate) {
                this.startDate = this.preorderDate;
            }

            if (this.configEstimationDays.length > 0) {
                this.updateDeliveryEstimate(this.configEstimationDays);
            } else if (this.zip && this.country && this.province) {
                this.estimateDelivery();
            }
        }

        parseDate(dateString) {
            // Date must be in dd/mm/yyyy format

            if (!dateString) return null;

            // Handle different separators (/, -, .)
            const parts = dateString.split(/[\/\-\.]/);

            if (parts.length !== 3) return null;

            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1; // Months are 0-based in JS
            const year = parseInt(parts[2], 10);

            if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

            return new Date(year, month, day);
        }

        estimateDelivery() {
            const params = new URLSearchParams();

            params.append("shipping_address[zip]", this.zip);
            params.append("shipping_address[country]", this.country);
            params.append("shipping_address[province]", this.province);

            fetch(`${window.routes?.cart_url}/shipping_rates.json?${params.toString()}`, { method: "GET" })
                .then(response => {
                    if (!response.ok) {
                        throw new Error("Error:", response.status);
                    }

                    return response.json();
                })
                .then(data => {
                    const shippingRates = data.shipping_rates || [];
                    const deliveryDays = shippingRates[0]?.delivery_days;

                    this.updateDeliveryEstimate(deliveryDays);
                })
                .catch(error => {
                    console.error("Error fetching delivery estimate", error);
                });
        }

        updateDeliveryEstimate(days = []) {
            const dateFormat = {
                month: "long",
                day: "numeric",
            }
            if (!this.hideYear) dateFormat.year = "numeric";

            let textOut = days.map(day => {
                const date = new Date(this.startDate);

                // giorni iniziali
                let remainingDays = day 
                // ciclo e aggiungo 1 in caso di giorno sabato e domenica (6 = sabato - 0 = domenica)ù
                while(remainingDays > 0){
                    date.setDate(date.getDate() + 1);
                    // controllo per aggiornare variabi in caso di sabato domenic 
                    const dayOfWeek = date.getDay();
                    dayOfWeek !== 0 && dayOfWeek !== 6 ? remainingDays-- : null
                    
                }
                
                return new Intl.DateTimeFormat(this.localeCountry, dateFormat).format(date);
            }).join(" - ");

            if (this.appendText) {
                textOut += ` ${this.appendText}`;
            }

            this.textContent = textOut;
        }

        parseConfigDays(daysData = "") {
            const days = [];

            daysData?.split("-")?.forEach(day => {
                const dayN = parseInt(day.trim());

                if (!isNaN(dayN) && dayN >= 0) {
                    days.push(dayN);
                }
            });

            return days;
        }
    };

    class OpScrollProgress extends HTMLElement {
        connectedCallback() {
            this.progressEl = this.querySelector("[data-scroll-progress]");
            this.container = document.querySelector(this.dataset.scrollContainer) || document.body;
            this.useHeaderOffset = this.dataset.headerOffset === "true";
            this.scrollOffset = this.useHeaderOffset ? document.querySelector(".js-headerGroup")?.offsetHeight || 0 : 0;

            if (!this.progressEl) return;

            this.updateScrollBar();
            window.addEventListener('scroll', this.updateScrollBar);
        };

        updateScrollBar = () => {
            const rect = this.container.getBoundingClientRect();
            const topWithOffset = rect.top - this.scrollOffset;
            const containerTop = topWithOffset < 0 ? Math.abs(topWithOffset) : 0;

            if (topWithOffset > 100 || containerTop > this.container.offsetHeight) return;

            const scrollPercent = Math.min((containerTop / this.container.offsetHeight) * 100, 100);
            const maxTop = this.offsetHeight - this.progressEl.offsetHeight;
            const top = Math.max(0, (this.offsetHeight * scrollPercent / 100));
            this.progressEl.style.top = `${Math.min(top, maxTop)}px`;
        }
    };

    class OpDialog extends HTMLElement {
        connectedCallback() {
            this.animationClass = this.dataset.animationClass || "";
            this.closeButtons = this.querySelectorAll(".js-op-modal-close");

            this.closeButtons.forEach(btn => btn.addEventListener("click", this.close));
            window.addEventListener("keydown", this.window_keydownHandler);
        }

        disconnectedCallback() {
            this.closeButtons.forEach(btn => btn.removeEventListener("click", this.close));
            window.removeEventListener("keydown", this.window_keydownHandler);
        }

        window_keydownHandler = (event) => {
            if (event.key?.toLowerCase() === "escape") {
                this.close();
            }
        }

        showModal = () => {
            this.dataset.open = "true";
            this.classList.remove("tw-invisible", "tw-opacity-0");
            if (this.animationClass) this.classList.add(this.animationClass);

            this.dispatchEvent(new CustomEvent("open"));
        }

        open = () => {
            this.dataset.open = "true";
            this.classList.remove("tw-invisible", "tw-opacity-0");
            this.classList.add("tw-animate-slide-left");

            this.dispatchEvent(new CustomEvent("open"));
        }
        close = () => {
            this.dataset.open = "false";
            this.classList.add("tw-invisible", "tw-opacity-0");
            if (this.animationClass) this.classList.remove(this.animationClass);
            this.dispatchEvent(new CustomEvent("close"));
        }

        disconnectedCallback() {
            this.querySelectorAll(".js-op-modal-close").forEach(btn => btn.removeEventListener("click", this.close));
        }
    }

    class OpSizeSwitcher extends HTMLElement {
        constructor() {
            super();
            this.unitElement = document.querySelector('.unit-measurement');
            this.checkbox = this.querySelector('.js-size-switch');
            this.mmLabel = this.querySelector('.js-mm-label .knob');
            this.inchLabel = this.querySelector('.js-inch-label .knob');
        }

        connectedCallback() {
            this.checkbox.addEventListener('change', this.updateToggleState);
            this.updateToggleState(true);
        }

        updateToggleState = (status) => {
            if (this.checkbox.checked && status != true) {
                this.mmLabel.classList.add('tw-bg-white');
                this.inchLabel.classList.remove('tw-bg-white');
                this.updateUnitMeasurement('mm');
            } else if (!this.checkbox.checked && status != true) {
                this.inchLabel.classList.add('tw-bg-white');
                this.mmLabel.classList.remove('tw-bg-white');
                this.updateUnitMeasurement('inch');
            } else if (status == true) {
                this.mmLabel.classList.add('tw-bg-white');
                this.inchLabel.classList.remove('tw-bg-white');
            }
        }

        updateUnitMeasurement(unit) {
            let sizeValues = document.querySelectorAll('.current-value');
            sizeValues.forEach((sizeValue) => {
                    var currentValue = parseInt(sizeValue.dataset.value);
                    if (currentValue != null && currentValue != '') {
                        if (unit == 'mm') {
                            currentValue = currentValue / 25.4;
                            sizeValue.dataset.value = currentValue.toFixed(2);
                            sizeValue.dataset.unit = unit;
                            sizeValue.innerHTML = currentValue.toFixed(0) + unit;
                        } else {
                            currentValue = currentValue * 25.4;
                            sizeValue.dataset.value = currentValue.toFixed(0);
                            sizeValue.dataset.unit = unit;
                            sizeValue.innerHTML = (currentValue / 100).toFixed(2) + unit;
                        }
                    }
                }
            )
        }

        disconnectedCallback() {
            this.checkbox.removeEventListener('change', this.updateToggleState);
        }
    }

    class OpModalEditorPreviewer extends HTMLElement {
        connectedCallback() {
            this.section = this.closest(`#${this.dataset.sectionId}`);
            this.modal = document.querySelector(`#${this.dataset.modalId}`);

            if (!this.section || !this.modal) return;

            this.section.addEventListener("shopify:section:select", () => {
                if (typeof this.modal.showModal === "function") {
                    if (this.modal.dataset.position === "right") this.modal.classList.add('tw-animate-slide-left');
                    this.modal.showModal();
                }
            });

            this.section.addEventListener("shopify:section:deselect", () => {
                if (typeof this.modal.close === "function") {
                    if (this.modal.dataset.position === "right") this.modal.classList.remove('tw-animate-slide-left');
                    this.modal.close();
                }
            });
        }
    }

    class OpBackBtn extends HTMLElement {
        connectedCallback() {
            this.parentHref = this.dataset.parentHref;
            this.addEventListener("click", this.handleClick);
        }

        disconnectedCallback() {
            this.removeEventListener("click", this.handleClick);
        }

        handleClick = () => {
            if (document.referrer?.startsWith(window.location.origin) && document.referrer !== window.location.href && window.history.length > 1) {
                window.history.back();
                return;
            }

            if (this.parentHref) {
                document.location.href = this.parentHref;
            }
        }
    }

    class OpCharacterLimiter extends HTMLElement {
        connectedCallback() {
            this.limit = parseInt(this.dataset.limit) || 0;
            this.readMoreBtn = this.querySelector(".js-read-more");
            this.visibleSpan = this.querySelector(".js-text");
            this.hiddenSpan = this.querySelector(".js-hidden-text");
            this.readMoreStr = this.dataset.readMore || "Read more";
            this.readLess = this.dataset.readLess || "Read less";

            if ((!this.visibleSpan || !this.readMoreBtn || !this.hiddenSpan || this.limit <= 0) || (this.visibleSpan.textContent.length <= this.limit)) {
                this.readMoreBtn?.remove();
                return;
            }

            this.setupTexts();
            this.readMoreBtn.addEventListener("click", () => {
                this.hiddenSpan.classList.toggle("tw-hidden")
                this.readMoreBtn.textContent = this.hiddenSpan.classList.contains("tw-hidden") ? this.readMoreStr : this.readLess;
            });
        }

        setupTexts = () => {
            const stringArray = this.visibleSpan.textContent.split(" ");
            let characterAccumulator = 0;
            let indexToSlice = 0;
            for (let index = 0; index < stringArray.length; index++) {
                const word = stringArray[index];
                characterAccumulator += word.length + 1; // +1 for space
                if (characterAccumulator > this.limit) {
                    indexToSlice = index;
                    break;
                }
            }

            this.visibleSpan.textContent = stringArray.slice(0, indexToSlice).join(" ");
            this.hiddenSpan.textContent = " " + stringArray.slice(indexToSlice).join(" ");
        }
    }

    class OpDynamicProductCard extends HTMLElement {

        constructor(){
            super();
            this.fetchCard = false;
        }

        static get observedAttributes() {
            return ['data-product-handle'];
        }

        attributeChangedCallback(name, oldValue, newValue) {
            if (oldValue != newValue && newValue) {
                this.productHandle = newValue;
                this.productCardFetchUrl = `${Shopify.routes.root}products/${this.productHandle}?view=op-product-card`;
                
                if(!this.fetchCard){
                    this.fetchCard = true;
                    this.fetchProductCard();
                }
            }
        }

        connectedCallback() {
            this.productHandle = this.dataset.productHandle;
            if (!this.productHandle) return;
            this.productCardFetchUrl = `${Shopify.routes.root}products/${this.productHandle}?view=op-product-card`;
            
            this.showOnLoad = this.dataset.showOnLoad === "true";
            if(!this.fetchCard){
                this.fetchCard = true;
                this.fetchProductCard();
            }
        }

        async fetchProductCard() {
            try {
                const response = await fetch(this.productCardFetchUrl);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const text = await response.text();
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'text/html');
                const cardElement = doc.querySelector('#op-collection-product-card');
                if (cardElement) {
                    // if (typeof trackSearchAttribution === "function") cardElement.addEventListener("click",()=>{trackSearchAttribution()});
                    this.innerHTML = ""; // Clear existing content
                    this.append(cardElement);
                    // this.cardFetched = true;
                    this.fetchCard = false;
                    this.toggleAttribute("data-loaded", true);

                    if (this.showOnLoad) {
                        this.style.display = "block";
                    }
                }
            } catch (error) {
                console.error('Error fetching product card:', error);
                this.remove(); // Remove custom element if fetch fails
            }
        }
    }

    class VMMVPdp extends HTMLElement {
        constructor() {
            super();

            // Container
            this.container = this.querySelector(`.vmmv-container`);
            this.buttonTryOn = document.querySelector('[data-button-tryon]');
            this.updSelected = this.container.dataset.upcSelected;
            this.upcList = [];
            this.onlyUpcList = [];
            this.wishlist = [];
            this.testWishlist = [];
            this.prodIdVariantId = [];
            this.resultTransitions = [];
            this.opWishlist = document.querySelector('op-wishlist');
            this.VMInitParamsKey = 'dc9ab158-7aa6-4ef2-bcbb-fad4a0591d4a'

            this.VMInitParams = {
                'source': 'VMMV_OP',
                'key': this.VMInitParamsKey,
                'channel': 'ecommerce',
                'brand': 'op',
            };

            this.region = 'EU'
            this.locale = Shopify.locale+'-'+Shopify.country
            if (this.locale == 'en-US' || this.locale == 'es-US'){
                this.region = 'US'
            }
            if (this.locale == 'en-AU' || this.locale == 'en-NZ'){
                this.region = 'AU'
            }

            this.store = {
                'id': '',
                'type': '',
                region: this.region,
            };

            if (this.container) {

                this.upcList = this.container.dataset.upcList.split('||');
                this.upcList.forEach(stringUpc => {

                    this.dettUpc = stringUpc.split('#_#');
                    this.brand = {
                        'name': this.dettUpc[5],
                        'logoUrl': this.dettUpc[6]
                    };

                    this.onlyUpcList.push(this.dettUpc[0]);

                    this.prodIdVariantId[this.dettUpc[1]] = {
                        'product': this.dettUpc[13],
                        'handle': this.dettUpc[14]
                    };

                    this.testWishlist[this.dettUpc[1]] = {
                        'product_id': this.dettUpc[13],
                        'variant_id': this.dettUpc[1],
                        'handle': this.dettUpc[14],
                        'upc': this.dettUpc[0],
                    }
                });
            }



        }

        connectedCallback() {

            this.checkUpcAvailability(this.onlyUpcList,this.updSelected).then(result => {
                this.resultTransitions = result;
            });
            /*
            document.addEventListener("la:dn:product:discount:calculated", (event) => {
                console.log('Offers loaded', event);
                if (event.detail.data[0].discountedPrice > 0) {
                    this.upcDiscounted = event.detail.data[0].discountedPrice / 100;
                } else {
                    this.upcDiscounted = event.detail.data[0].discountedPrice;
                }
            });
            */

            this.tryOnUnsubscriber = subscribe(PUB_SUB_EVENTS.startTryOn, (event) => {
                if(event.source != "try-on-pdp") {
                    return;
                }
                if (this.container) {
                    this.prices = [];
                    this.prodList = [];
                    //let isFirstItem = true
                    //let discountedPrice = this.upcDiscounted;
                    let discountedPrice = 0
                    this.upcList.forEach(stringUpc => {
                        this.dettUpc = stringUpc.split('#_#');
                        if(this.resultTransitions[this.dettUpc[0]].available == true){


                            let prodItem = {
                                'upc': this.dettUpc[0],
                                'code': this.dettUpc[1],
                                'name': this.dettUpc[2],
                                'colorCode': this.dettUpc[3],
                                'thumbnailUrl': this.dettUpc[4],
                                'brand': this.brand,
                                'size': this.dettUpc[9],
                                'lensColorLabel': this.dettUpc[8],
                                'frameColorLabel': this.dettUpc[7]
                            };

                            if(this.resultTransitions[this.dettUpc[0]].transitions == true){
                                prodItem.isTransition = true;
                                prodItem.transitionLens = {
                                    defaultColor: 'SIGNATURE-GEN8-AMBER',
                                    defaultType: 'SIGNATURE',
                                    defaultAlphaLevel: 0.5,
                                    availableColors: [
                                        'SIGNATURE-GEN8-AMBER',
                                        'SIGNATURE-GEN8-AMETHYST',
                                        'SIGNATURE-GEN8-EMERALD',
                                        'SIGNATURE-GEN8-GRAY',
                                        'SIGNATURE-GEN8-SAPPHIRE',
                                        'SIGNATURE-GEN8-BROWN',
                                        'SIGNATURE-GEN8-GGREEN'
                                    ]
                                };

                            }

                            this.prodList.push(prodItem);
                            this.prices.push({
                                upc: this.dettUpc[0],
                                currency: this.dettUpc[12],
                                current: { text: this.dettUpc[10], value: parseFloat(this.dettUpc[10]) }
                                /*
                                ...(discountedPrice>0 && discountedPrice != parseFloat(this.dettUpc[10])
                                    ? {
                                        current: { text: String(discountedPrice), value: parseFloat(discountedPrice) },
                                        previous: { text: this.dettUpc[10], value: parseFloat(this.dettUpc[10]) }
                                    }
                                    : {
                                        current: { text: this.dettUpc[10], value: parseFloat(this.dettUpc[10]) }
                                    }
                                )
                                */
                            });
                        }
                        //isFirstItem = false
                        //discountedPrice = 0;
                    });
                    
                }
                // Init object with settings
                this.initInput = {
                    'selector': '.vmmv-container',
                    'locale': this.locale,
                    'products': this.prodList,
                    'store': this.store,
                    'prices':this.prices,
                    'vmInit': this.VMInitParams,
                    'style': '',
                    fromStore: true,
                    isTryOnEnabled: true,
                    isPictureModeEnabled: true,
                    isUploadPictureEnabled: true,
                    isVideoModeEnabled: true,
                    isTakeScreenshotEnabled: true
                };

                console.log('this.initInput',this.initInput)
                let elem = document.querySelector('op-pdp-vmmv');
                if (elem) {
                    elem.classList.remove('tw-hidden');
                    elem.style.background = "var(--Alpha-Black-Dark, rgba(0, 0, 0, 0.50))";
                    elem.style.backdropFilter = "blur(11px)";
                    document.querySelector('body').style.overflowY = "hidden";
                }
                let vmapp = null

                Object.values(this.testWishlist).forEach(async variantWish => {

                    const isInWishlist = await OpWishlistUtils.isItemInWishlist(variantWish['variant_id'])
                    if (isInWishlist) { this.wishlist.push(variantWish['upc']); }
                });

                const config = {
                    ...this.initInput,
                    transitionFeature: {
                        enabled: true
                    },
                    onClose: async () => {
                        if (elem) {
                            elem.classList.add('tw-hidden');
                            elem.style.background = "";
                            elem.style.backdropFilter = "";
                        }
                        await vmapp.unmount();
                        console.log("chiudo");
                        document.querySelector('body').style.overflowY = "";
                        this.container.innerHTML = '';
                    },
                    onPrivacyReject: async () => {
                        await config.onClose();
                        await vmapp.unmount();
                    },
                    onAddToBag: async (product, frameSize) => {

                        // add to cart
                        let formData = {
                            'id': product.code,
                            'quantity': 1,
                            'cart_added_at': Date.now(),
                        };
                        try {
                            let url_fetch = window.Shopify.routes.root + 'cart/add.js';
                            url_fetch = url_fetch + '?sections=cart-drawer,cart-icon-bubble,op-cart-drawer-product';

                            const response = await fetch(url_fetch, {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify(formData)
                            });
                            const responseData = await response.json();
                            if (response.ok) {
                                this.cart = document.querySelector('cart-drawer');
                                this.cart.classList.remove('is-empty');
                                this.cart.renderContents({
                                    id: responseData.id,
                                    sections: responseData.sections,
                                });

                                this.cart.classList.add('active', 'animate');
                                //await config.onClose();
                            } else {
                                console.log('Error:', responseData.message);
                            }

                            publish(PUB_SUB_EVENTS.cartUpdate, {
                                source: 'product-form',
                                productVariantId: product.code,
                                productHandle: this.prodIdVariantId[product.code].handle,
                                cartData: responseData
                            });
                        } catch (error) {
                            console.error('Error:', error);
                        }
                    },
                    wishlistedUpcs: this.wishlist,
                    onToggleWishlist: async (product) => {
                        const wlItem = {
                            productId: this.prodIdVariantId[product.code]['product'],
                            productHandle: this.prodIdVariantId[product.code]['handle'],
                            variantId: product.code
                        }

                        const inWl = await OpWishlistUtils.isItemInWishlist(product.code)
                        await OpWishlistUtils.updateWishlist(inWl ? WISHLIST_CONSTANTS.eventKeyRemove : WISHLIST_CONSTANTS.eventKeyAdd, wlItem )
                    }
                }

                vmapp = new vmmv.VMWidgetApp()
                console.log('vmapp',vmapp)

                vmmv.warmUp();
                vmapp.setConfig(config)
                    .then(() => {
                        vmapp.init()
                    })
                    .catch(e => {
                        console.error(e)
                    })
            });

        }

        async checkUpcAvailability (upcs,upcSelected) {
            // All configuration params are written below
            let resultTransitions = [];
            let upcAvailability = await vmmv.isUPCSupported(upcs,this.VMInitParamsKey)
            let isBrowserSupported = await vmmv.isBrowserSupported();
            let browserSupported = isBrowserSupported ? "1" : "0";
            let UpcSupported = "0";

            Object.entries(upcAvailability).forEach(([upc, upcAvailabilityResponse]) => {
                resultTransitions[upc] = {
                    transitions: upcAvailabilityResponse.transitions,
                    available: upcAvailabilityResponse.available
                };
                if(upcAvailabilityResponse.available == true && upc == upcSelected){
                    UpcSupported = "1";
                    if (browserSupported == "1") {
                        document.querySelector('[data-button-tryon]').classList.remove('tw-hidden');
                    }
                }
            });
            publish(PUB_SUB_EVENTS.VMMVAvailability, {
                'Vm_IsUpcSupported': UpcSupported,
                'Vm_IsBrowserSupported': browserSupported,
                'Vm_PageContext': 'PDP',
            });

            return resultTransitions;

        }
    }
    class OpTryOnButton extends HTMLElement {
        constructor() {
            super();
        }

        connectedCallback() {
            this.addEventListener('click',(event)=>{
                publish(PUB_SUB_EVENTS.startTryOn, {
                    source:'try-on-pdp'
                });
            });

            this.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    this.click();
                }
            });
        }
    }

    class RadioSelector extends HTMLElement {

        connectedCallback() {
            this.section = this.closest(this.dataset.sectionId);
            this.variantUrl = this.dataset.variantUrl;
            this.btn = this.section.querySelector('.js-variant-btn');
            // this.btn.href = this.variantUrl;
            this.radio = this.querySelector('input[type="radio"]');
            this.preselected = this.dataset.preselected === 'true';
            this.radio.addEventListener('change', this.changeHandler);
            this.btn.addEventListener("click", () => {
                this.btn.closest("dialog").close()
            })
        }

        // changeHandler = () => {
        //     if (this.radio.checked) {
        //         this.btn.href = this.variantUrl;
        //         /*this.btn.classList.toggle("tw-op-btn-filled-disabled", this.preselected);
        //         this.btn.classList.toggle("tw-pointer-events-none", this.preselected);
        //         */
        //     };

        // }
    }

    class OpDiscountNinjaPrice extends HTMLElement {
        connectedCallback() {
            window.opTealiumPrices = window.opTealiumPrices || [];
            this.pdHandle = this.dataset.laDnProductHandle;
            this.extractPrice();
        }

        async extractPrice() {
            let priceObj = {};

            await ObserverUtils.waitForElement('[data-limoniapps-discountninja-price-cents-presentmentcurrency]', this, 5000)
                .then(() => {
                    this.priceElements = this.querySelectorAll('[data-limoniapps-discountninja-price-cents-presentmentcurrency]');

                    // Extract prices from the elements
                    const prices = [...this.priceElements].map(el => {
                        const priceString = el.querySelector('.money').textContent.trim();
                        return GeneralUtils.parseCurrency(priceString);
                    });

                    priceObj = {
                        price: Math.min(prices[0]?.value, prices[1]?.value),
                        priceFull: Math.max(prices[0]?.value, prices[1]?.value)
                    };
                })
                .catch(() => {
                    const pdPriceFull = GeneralUtils.parseCurrency(this.dataset.laDnProductPrice).value;
                    priceObj =  {
                        price: pdPriceFull,
                        priceFull: pdPriceFull,
                    };
                })
                .finally(() => {
                    priceObj.productHandle = this.pdHandle;
                    if (!window.opTealiumPrices.find(item => item.productHandle === this.pdHandle)) window.opTealiumPrices.push(priceObj);
                });
        }
    }

    class OpProgressIndicator extends HTMLElement {
        static observedAttributes = ["value", "max"];

        connectedCallback() {
            this.progressBar = this.querySelector('.js-progress-bar');
            if (!this.progressBar) {
                console.error("Progress bar element not found in OpProgressIndicator.", this);
                return;
            }
            this.updateProgress();
        }

        attributeChangedCallback() {
            this.updateProgress();
        }

        updateProgress() {
            const progress = parseFloat(this.getAttribute("value")) || 0;
            const max = parseFloat(this.getAttribute("max")) || 100;
            const percentualProgress = (progress * 100) / max;
            if (this.progressBar) this.progressBar.style.width = `${percentualProgress}%`;
        }

        startLoading() {
            this.loadingInterval = setInterval(() => {
                this.setAttribute("value", Math.min(parseFloat(this.getAttribute("value")) + 10, parseFloat(this.getAttribute("max")) || 100));
                if (parseFloat(this.getAttribute("value")) >= parseFloat(this.getAttribute("max"))) clearInterval(this.loadingInterval);
            }, 50);
        }
    }

    class OpStripNewsletter extends HTMLElement {
        connectedCallback() {
            this.sectionSelecorId = this.dataset.sectionSelectorId;
            this.closeButtoon = this.querySelector('[data-close]');

            this.closeButtoon?.addEventListener('click', (event) => {
                this.classList.add('hidden'); 
            });

            const section = document.querySelector('#' + this.sectionSelecorId);

            this.interecating = new Set();

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(e => {
                    if (e.isIntersecting) {
                        this.interecating.add(e.target);
                    } else {
                        this.interecating.delete(e.target);
                    }
                });
                
                const isIntersectingAnything = this.interecating.size > 0;
                section.classList.toggle('hide-on-bottom', isIntersectingAnything);

            }, {
                root: null,
                threshold: 0.5,
                rootMargin: "0px"
            })

            this.observer.observe(document.querySelector(".op-hero"));
            document.querySelectorAll(".shopify-section-group-footer-group").forEach(el => {
                this.observer.observe(el)
            })
        }

        disconnectedCallback(){
            this.observer.disconnect()
        }
    }

    class OpDinamicSlider extends HTMLElement {

        constructor() {
            super();
        }

        connectedCallback() {
            //Blocks
            this.contentBlocks = this.querySelectorAll("[data-content-block-id]");
            // Swiper
            this.swiperElement = this.querySelector(`.swiper`);
           

            this.slides = this.querySelectorAll('.swiper-slide');
            this.currSlide = this.slides[0];
            this.pagination = this.querySelectorAll('.swiper-pagination-bullet');
            if(this.pagination){
                this.pagination.forEach(pagination => {
                    pagination.addEventListener('click', this.pagination_clickHandler);
                });
            }

            this.slides.forEach(slide => {
                slide.addEventListener('click', this.slide_clickHandler);
            });

            this.buttonSwiper = window.Swiper ? new window.Swiper(this.swiperElement, {
                slidesPerView: "auto",
                slidesPerGroupAuto: true,
                navigation: {
                    nextEl: '[data-swiper-btn-next]',
                    prevEl: '[data-swiper-btn-prev]',
                },
                updateOnWindowResize: true,
                loop: false,
                grabCursor: true,
                centeredSlidesBounds: true,
                simulateTouch: true,
                slideToClickedSlide: true,
                pagination: {
                    el: ".swiper-pagination",
                },
            }) : null;
            
            if (!this.buttonSwiper) {
                console.warn("Swiper is not available.");
            }
        }

        disconnectedCallback () {
            this.slides?.forEach(slide => {
                slide.removeEventListener('click', this.slide_clickHandler);
            });
            this.buttonSwiper?.destroy();
        }

        handleOnRendered () {

            if (this.slides.length > 0) {
                this.slides.forEach((slide, index) => {
                    slide.toggleAttribute("data-active", index === 0);
                });
            }

            this.contentBlocks.forEach((block, index) => {
                block.toggleAttribute("data-active", index === 0);
            });
        }

        pagination_clickHandler = (event) => {
            let currSlide;

            if (!this.isRtrRendered) {
                return;
            }

            currSlide = this.querySelector('.swiper-slide-active');
            if (!currSlide) {
                console.warn("Current slide not found.");
                return;
            }

            this.updateContentBlock(currSlide.dataset.blockId);
            this.buttonSwiper?.slideTo(this.buttonSwiper.clickedIndex);
        }

        slide_clickHandler = (event) => {
            let currSlide;
            this.slides.forEach(slide => {
                if (slide.toggleAttribute("data-active", slide === event.currentTarget)) {
                    this.currSlide = slide;
                    currSlide = slide;
                }
            });

            if (!currSlide) {
                console.warn("Current slide not found.");
                return;
            }

            this.updateContentBlock(currSlide.dataset.blockId);
            this.buttonSwiper?.slideTo(this.buttonSwiper.clickedIndex); // Ensures direct navigation
        }

        updateContentBlock (currentBlockId) {
            this.contentBlocks.forEach(block => {
                block.toggleAttribute("data-active", block.dataset.contentBlockId === currentBlockId);
            });
        }
    }

    function setupCustomElements () {

        
        if (!customElements.get('op-try-on-button')) {
            customElements.define("op-try-on-button", OpTryOnButton);
        }
        if (!customElements.get('op-video-with-thumbnail')) {
            customElements.define("op-video-with-thumbnail", OpVideoWithThumbnail);
        }
        if (!customElements.get('op-form')) {
            customElements.define("op-form", OpForm);
        }
        if (!customElements.get('op-password-checker')) {
            customElements.define("op-password-checker", OpPasswordChecker);
        }
        if (!customElements.get('op-quantity-input')) {
            customElements.define("op-quantity-input", OpQuantitySelector);
        }
        if (!customElements.get("op-tabs")) {
            customElements.define("op-tabs", OpTabs);
        }
        if (!customElements.get("op-rangeslider")) {
            customElements.define("op-rangeslider", OpRangeSlider);
        }
        if (!customElements.get('op-text-field')) {
            customElements.define("op-text-field", OpTextField);
        }
        if (!customElements.get('op-slideshow')) {
            customElements.define("op-slideshow", OpSlideShow);
        }
        if (!customElements.get('op-countdown')) {
            customElements.define("op-countdown", OpCountdown);
        }
        if (!customElements.get("op-modal-trigger")) {
            customElements.define("op-modal-trigger", OpModalTrigger);
        }
        if (!customElements.get('op-slider-component')) {
            customElements.define('op-slider-component', OpSliderComponent);
        }
        if (!customElements.get('op-swiper')) {
            customElements.define('op-swiper', OpSwiper);
        }
        if (!customElements.get('op-password-checker-container')) {
            customElements.define("op-password-checker-container", OpPasswordCheckerContainer);
        }
        if (!customElements.get("op-modal-closer")) {
            customElements.define("op-modal-closer", OpModalCloser);
        }
        if (!customElements.get("op-intersection-sticky")) {
            customElements.define("op-intersection-sticky", OpIntersectionSticky);
        }
        if (!customElements.get("op-delivery-estimator")) {
            customElements.define("op-delivery-estimator", OpDeliveryEstimator);
        }
        if (!customElements.get('op-scroll-progress')) {
            customElements.define("op-scroll-progress", OpScrollProgress);
        }
        if (!customElements.get('modal-or-link-button')) {
            customElements.define('modal-or-link-button', ModalOrLinkButton);
        }
        if (!customElements.get('op-dialog')) {
            customElements.define("op-dialog", OpDialog);
        }
        if (!customElements.get('op-size-switcher')) {
            customElements.define("op-size-switcher", OpSizeSwitcher);
        }
        if (!customElements.get('op-modal-editor-previewer')) {
            customElements.define("op-modal-editor-previewer", OpModalEditorPreviewer);
        }
        if (!customElements.get('op-back-btn')) {
            customElements.define("op-back-btn", OpBackBtn);
        }
        if (!customElements.get('op-pdp-vmmv')) {
            customElements.define('op-pdp-vmmv', VMMVPdp);
        }
        if (!customElements.get('op-character-limiter')) {
            customElements.define("op-character-limiter", OpCharacterLimiter);
        }
        if (!customElements.get('op-dynamic-product-card')) {
            customElements.define("op-dynamic-product-card", OpDynamicProductCard);
        }
        if (!customElements.get('radio-selector')) {
            customElements.define("radio-selector", RadioSelector);
        }
        if (!customElements.get('op-newsletter-form')) {
            customElements.define('op-newsletter-form', OpNewsletterForm);
        }
        if (!customElements.get('op-discount-ninja-price')) {
            customElements.define('op-discount-ninja-price', OpDiscountNinjaPrice);
        }
        if (!customElements.get('op-progress-indicator')) {
            customElements.define('op-progress-indicator', OpProgressIndicator);
        }
        if (!customElements.get('op-strip-newsletter')) {
            customElements.define('op-strip-newsletter', OpStripNewsletter);
        }
        if (!customElements.get('op-dinamic-slider')) {
            customElements.define('op-dinamic-slider', OpDinamicSlider);
        }
    }        

    


    

    function setupListeners() {
        document.querySelectorAll(".js-op-modal").forEach((modal) => {
            modal.addEventListener("click", (event) => {
                const isBackdrop = event.target === modal;
                const backdropClickClose = modal.getAttribute("data-backdrop-close") === "true";

                if (isBackdrop && backdropClickClose && typeof modal.close === "function") {
                    modal.classList.remove('tw-animate-slide-left');
                    console.log('close from overlay');
                    
                    modal.close();
                }
            });
        });

        document.querySelectorAll(".js-op-modal-close").forEach((btn) => {
            btn.addEventListener("click", () => btn.closest("dialog")?.close());
        });

        document.querySelectorAll(".js-op-modal-btn").forEach((btn) => {
            btn.addEventListener("click", () => {
                const modalId = btn.getAttribute("data-modal-id");
                const target = modalId ? document.getElementById(modalId) : null;
                if (target && typeof target.showModal === "function") {
                    if (target.dataset.position === "right") target.classList.add('tw-animate-slide-left');
                    target.showModal();
                }
            });

            btn.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    btn.click();
                }
            });
        });

        document.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
            if (checkbox.getAttribute("data-indeterminate") === "true") {
                checkbox.indeterminate = true;
            }
        });

        document.querySelectorAll(".js-op-draggable").forEach((elmnt) => {
            let pos1 = 0, pos2 = 0, pos3 = 0, pos4 = 0;

            const dragMouseDown = (e) => {
                e.preventDefault();

                pos3 = e.clientX;
                pos4 = e.clientY;

                document.addEventListener("mouseup", closeDragElement);
                document.addEventListener("mousemove", elementDrag);
            }

            const elementDrag = (e) => {
                e.preventDefault();

                pos1 = pos3 - e.clientX;
                pos2 = pos4 - e.clientY;
                pos3 = e.clientX;
                pos4 = e.clientY;

                elmnt.style.top = (elmnt.offsetTop - pos2) + "px";
                elmnt.style.left = (elmnt.offsetLeft - pos1) + "px";
                elmnt.style.position = "absolute";
            }

            const closeDragElement = () => {
                document.removeEventListener("mouseup", closeDragElement);
                document.removeEventListener("mousemove", elementDrag);
            }

            const restorePos = (e) => {
                e.preventDefault();

                elmnt.style.removeProperty("position");
                elmnt.style.removeProperty("top");
                elmnt.style.removeProperty("left");
            }

            elmnt.addEventListener("mousedown", dragMouseDown);
            elmnt.addEventListener("contextmenu", restorePos);
        });

        document.querySelectorAll(".js-customerLogin").forEach((form) => {
            form.addEventListener("submit", async (event) => {
 
                const fdata = new FormData(form);
                const submitButtons = form.querySelectorAll("[type='submit']");
                const storefrontUrl = form.getAttribute("data-storefront-url");
                const storefrontToken = fdata.get("storefront_token");

                const input = {
                    email: fdata.get("customer[email]"),
                    password: fdata.get("customer[password]")
                }

                event.preventDefault();
                submitButtons.forEach(btn => btn.disabled = true);

                console.log("Submitting customer login form", input);

                try {
                    const response = await fetch(storefrontUrl, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Shopify-Storefront-Access-Token": storefrontToken
                        },
                        body: JSON.stringify({
                            query: `#graphql
                                mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
                                    customerAccessTokenCreate(input: $input) {
                                        customerAccessToken {
                                            accessToken
                                        }
                                        customerUserErrors {
                                            message
                                        }
                                    }
                                }
                            `,
                            variables: { input }
                        })
                    });

                    const result = await response.json();
                    const token = result.data?.customerAccessTokenCreate?.customerAccessToken;

                    if (result.error) {
                        console.error(result.error);
                    }

                    console.log("token", token)

                    if (token) {
                        sessionStorage.setItem("customerAccessToken", token.accessToken);
                    }
                }
                catch (error) {
                    console.error(error);
                }

                form.submit();


            });
        });

        document.querySelectorAll(".js-showPickerLb").forEach((lb) => {
            lb.addEventListener("click", () => {
                const targetId = lb.getAttribute("for");
                const targetEl = targetId ? document.getElementById(targetId) : null;

                if (targetEl && targetEl.showPicker && !targetEl.disabled && !targetEl.readOnly) {
                    targetEl.showPicker();
                }
            });
        });
    }


    setupCustomElements();
    setupListeners();
}

opComponents();
